<template>
  <div class="flex flex-col items-center justify-center h-screen w-full overflow-hidden">
    <!-- Login Form -->
    <img :src="logoSrc" alt="Logo" height="300" class="h-[170px] mr-2 backlight" />
    <div class="logo-title roboto">After Hours Requests</div>
    <Card class="w-64">
      <template #content>
        <form @submit.prevent="handleLogin">
          <div class="mb-6">
            <FloatLabel variant="on">
              <InputText id="username" v-model="username" required class="w-full" autocomplete="off" />
              <label for="username">Email</label>
            </FloatLabel>
          </div>
          <div class="mb-6">
            <FloatLabel variant="on">
              <InputText id="password" v-model="password" required class="w-full" type="password" autocomplete="off" />
              <label for="password">Password</label>
            </FloatLabel>
          </div>
        <div class="flex flex-column align-items-center justify-content-center">
          <Button label="Login" type="submit" class="submit-button mt-2" :loading="isLoading" severity="primary600" />
          <!-- <Button label="Register" text class="mt-2" :loading="isLoading" severity="primary" @click="testCall" /> -->
        </div>
        </form>
        <div class="flex flex-col items-center justify-content-center">
        <Button :icon="themeIcon" class="mt-4  theme-toggle-btn" severity="secondary" text @click="toggleTheme" />
      </div>
      </template>
    </Card>

    <Toast />
    <!-- <Button label="Show" @click="show" /> -->
  </div>
</template>

<script>
import { ref, onMounted, computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Card from 'primevue/card';
import FloatLabel from 'primevue/floatlabel';

import { useToast } from 'primevue/usetoast';
//import { inject } from 'vue';
import api from '@/utils/axios';

export default {
  components: { Button, InputText, Password, Card, FloatLabel },
  setup() {
    const toast = useToast();
    const store = useStore();
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const isLoading = ref(false);

    const loginAttempts = ref(0);
    const lastAttemptTime = ref(null);
    const isLocked = ref(false);


    // const api = axios.create({
    //   baseURL: apiUrl,
    //   headers: {
    //       'Content-Type': 'application/json',
    //   },
    //   //withCredentials: true
    // });



    const checkRateLimit = () => {
      const now = Date.now();
      // Reset attempts if 15 minutes have passed
      if (lastAttemptTime.value && (now - lastAttemptTime.value) > 15 * 60 * 1000) {
        loginAttempts.value = 0;
        isLocked.value = false;
      }

      if (isLocked.value) {
        const remainingTime = Math.ceil((15 * 60 * 1000 - (now - lastAttemptTime.value)) / 60000);
        toast.add({
          severity: 'error',
          summary: 'Rate Limited',
          detail: `Too many login attempts. Please try again in ${remainingTime} minutes.`,
          life: 5000
        });
        return false;
      }

      return true;
    };



    const toggleTheme = () => {
      const newTheme = currentTheme.value === "dark" ? "light" : "dark";
      currentTheme.value = newTheme;
      document.documentElement.classList.toggle("dark-mode");
      store.dispatch('changeTheme', newTheme);
    };

    const handleLogin = async () => {
      if (!checkRateLimit()) return;

      try {
        isLoading.value = true;
        loginAttempts.value++;
        lastAttemptTime.value = Date.now();

        console.log('Login attempt:', { email: username.value });
        
        const response = await api.post('/users/login', {
          email: username.value,
          pass: password.value
        });

        console.log('Login response:', response);
        console.log('Login response data:', response.data);
        console.log('Login response status:', response.status);
        console.log('Login response headers:', response.headers);

        const data = response.data;

        if (data.success) {
          // Reset attempts on successful login
          loginAttempts.value = 0;
          isLocked.value = false;

          // Pass user data directly since userData is not in response
          await store.dispatch('users/loginUser', data);

          toast.add({
            severity: 'success',
            summary: 'Welcome!',
            detail: 'Login successful',
            life: 3000
          });
          router.push('/dashboard');
        } else {
          console.log('Login failed - response data:', data);
          console.log('Login failed - response type:', data.type);
          console.log('Login failed - response message:', data.message);
          
          // Handle rate limit response from backend
          if (data.type === 'rate_limit') {
            isLocked.value = true;
            toast.add({
              severity: 'error',
              summary: 'Rate Limited',
              detail: data.message,
              life: 5000
            });
            return;
          }

          const errorMessage = data.message || 'Login failed';
          toast.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: errorMessage,
            life: 3000
          });
        }
      } catch (error) {
        console.error('Login error:', error);
        console.error('Login error response:', error.response);
        console.error('Login error response data:', error.response?.data);
        console.error('Login error response status:', error.response?.status);
        console.error('Login error response headers:', error.response?.headers);
        console.error('Login error message:', error.message);
        console.error('Login error code:', error.code);
        
        // Enhanced error handling
        const errorMessage = error.code === 'ERR_NETWORK' 
          ? 'Server connection failed. Please check your network connection.'
          : error.response?.data?.message || 'Login failed. Please try again later.';
          
        toast.add({
          severity: 'error',
          summary: 'Connection Error',
          detail: errorMessage,
          life: 5000
        });
      } finally {
        isLoading.value = false;
      }
    };



    const logoSrc = computed(() => {
      return require('@/assets/logo_icon.svg');
    });

    const currentTheme = ref(localStorage.getItem('isTheme') || 'light');

    const themeIcon = computed(() => {
      return currentTheme.value === 'dark' ? 'pi pi-sun' : 'pi pi-moon';
    });

    onMounted(() => {
      //fetchClientStats();
      store.dispatch('initializeTheme').then(() => {
        const isTheme = localStorage.getItem('isTheme');
        currentTheme.value = isTheme || 'light';
        if (isTheme === 'dark') {
          document.documentElement.classList.add("dark-mode");
        } else {
          document.documentElement.classList.remove("dark-mode");
        }
      });
    });

    return {username, password, isLoading, handleLogin, toggleTheme, logoSrc, themeIcon };
  },
};
</script>

<style scoped>
.login-form {
  width: 100%;
  max-width: 400px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.form-group {
  margin-bottom: 20px;
}

.submit-button {
  width: 100%;
}

.logo-title {
  font-size: 2em;
  margin-bottom: 20px;
  font-weight: 900;
}

.footer-text {
  color: var(--p-gray-400);
}

.dark-mode .footer-text {
  color: var(--p-gray-500);
}

/* FloatLabel стили для лучшего отображения */
:deep(.p-float-label) {
  margin-bottom: 1rem;
}

:deep(.p-float-label label) {
  color: var(--p-text-color-secondary);
  transition: all 0.2s ease-in-out;
}

:deep(.p-float-label input:focus ~ label),
:deep(.p-float-label input.p-filled ~ label) {
  color: var(--p-primary-color);
}

/* Увеличенная иконка темы только на странице логина */
.theme-toggle-btn :deep(.p-button-icon) {
  font-size: 1.5rem !important;
}

.theme-toggle-btn {
  width: 3rem;
  height: 3rem;
}

/* Кнопка логина использует глобальные стили */
</style>
